import React, { useEffect, useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/solid';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import VoiceSelector from './VoiceSelector';

const PlayButtonComponent = ({ size = 85, isHotkeysEnabled = true, itemData = null, onVoiceChange = null, currentVoice = null, sessionSettings = null, hideSlider = false }) => {
  const { isPlaying, smoothedProgress, audioSpeed, setAudioSpeed, playSound, isBuffering, audioElement } = useAudioPlayer();
  
  // Determine if this is a word with voice options
  const isWord = itemData?.word && itemData?.sound_male && itemData?.sound_female;
  
  // Use currentVoice from parent if provided, otherwise use local state
  const [localSelectedVoice, setLocalSelectedVoice] = useState('male');
  const selectedVoice = currentVoice || localSelectedVoice;
  
  // Sync with parent's voice selection if available
  useEffect(() => {
    if (currentVoice && currentVoice !== localSelectedVoice) {
      setLocalSelectedVoice(currentVoice);
    }
  }, [currentVoice, localSelectedVoice, itemData?.word]);

  const radius = size * 0.4;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (smoothedProgress / 100) * circumference;
  
  // Check if audio is actually ready (has a source and not buffering)
  const isReady = audioElement?.src && !isBuffering;

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check both e.code (physical keyboards) and e.key (iOS virtual keyboard, where e.code is often "")
      const useBacktick = sessionSettings?.audioHotkeyUseBacktick;
      const targetCode = useBacktick ? 'Backquote' : 'Space';
      const targetKey = useBacktick ? '`' : ' ';

      if ((e.code === targetCode || e.key === targetKey) && isHotkeysEnabled && isReady) {
        e.preventDefault();
        playSound();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [playSound, isHotkeysEnabled, isReady, sessionSettings]);

  // Android virtual keyboards fire keydown with key: "Unidentified" for all keys (W3C IME spec, keyCode 229).
  // beforeinput is the only reliable event with a stable e.data value on Android virtual keyboards.
  // e.preventDefault() on beforeinput also actually prevents character insertion (unlike keydown on Android).
  // Physical keyboard: keydown fires first and calls e.preventDefault(), which suppresses the subsequent
  // beforeinput, so there is no double-play on desktop or iOS.
  // Japanese virtual keyboards (e.g. Gboard JA) insert a full-width ideographic space (U+3000) for the
  // space key, and a full-width backtick (U+FF40) in full-width mode, so accept those too.
  useEffect(() => {
    const handleBeforeInput = (e) => {
      if (!isHotkeysEnabled || !isReady) return;
      const useBacktick = sessionSettings?.audioHotkeyUseBacktick;
      const targetData = useBacktick ? ['`', '｀'] : [' ', '　'];
      if (targetData.includes(e.data)) {
        e.preventDefault();
        playSound();
      }
    };
    document.addEventListener('beforeinput', handleBeforeInput);
    return () => document.removeEventListener('beforeinput', handleBeforeInput);
  }, [playSound, isHotkeysEnabled, isReady, sessionSettings]);
  
  // Handle voice change
  const handleVoiceChange = (voice) => {
    setLocalSelectedVoice(voice);
    if (onVoiceChange) {
      onVoiceChange(voice);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`relative flex items-center justify-center ${isReady ? 'cursor-pointer' : 'cursor-wait'}`}
        style={{ width: `${size}px`, height: `${size}px` }}
        onClick={isReady ? playSound : undefined}
      >
        <svg className="absolute inset-0" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            className="text-gray-300 dark:text-gray-600"
            stroke="currentColor"
            strokeWidth="5"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className={`text-secondary transition-opacity duration-300 ${isPlaying || smoothedProgress > 0 ? 'opacity-100' : 'opacity-0'}`}
            stroke="currentColor"
            strokeWidth="5"
            strokeLinecap="round"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: strokeOffset,
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
            }}
          />
        </svg>
        <div
          className={`absolute flex items-center justify-center rounded-full shadow-lg transition-all ${
            isReady ? 'bg-primary hover:brightness-110' : 'bg-gray-400 dark:bg-gray-500'
          }`}
          style={{ inset: `${size * 0.1}px` }}
        >
          {/* ✅ The icon is now always the PlayIcon. */}
          <PlayIcon className="text-white" style={{ height: `${size / 2.5}px`, width: `${size / 2.5}px` }} />
        </div>
      </div>
      
      {isWord && (
        <VoiceSelector 
          selectedVoice={selectedVoice} 
          onVoiceChange={handleVoiceChange}
          disabled={!isReady}
        />
      )}

      {!hideSlider && (
        <div style={{ width: `${size}px` }} className="mt-2">
          <Slider
            min={0.5}
            max={1.5}
            step={0.1}
            value={audioSpeed}
            onChange={isReady ? setAudioSpeed : undefined}
            marks={{ [audioSpeed]: `${audioSpeed.toFixed(1)}x` }}
            styles={{
              track: { backgroundColor: 'var(--color-primary)' },
              rail: { backgroundColor: 'var(--color-border)' },
              handle: {
                backgroundColor: 'var(--color-background-secondary)',
                borderColor: 'var(--color-primary)',
                borderWidth: 2,
                boxShadow: 'none',
                opacity: 1,
                marginTop: -4,
              },
              mark: { top: 10 },
              markLabel: { color: 'var(--color-content-secondary)', fontSize: '12px' },
            }}
          />
        </div>
      )}
    </div>
  );
};

const PlayButton = React.memo(PlayButtonComponent);
export default PlayButton;
